import { isEmpty } from '@intelligence/v3/utils/common';
import {
  getDefaultApiFieldMap,
  getDefaultUrlFieldMap,
  getItemApiParamsFieldMap,
  getItemUrlParamsFieldMap,
} from '../shared/fieldMapUtils';
import { getRangePickerValue, getValidatedRangePickerValue } from './utils';

import type { IFilterMiddlewareUtils } from '../shared/types';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IRangePickerFilterValue, IRangePickerMiddlewareParams } from './types';

const getApiParamsFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  granularityField: string;
  startDateField: string;
  endDateField: string;
} => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);

  return {
    granularityField: apiParamsFieldMap?.granularity || defaultApiFieldMap.granularity || 'granularity',
    startDateField: apiParamsFieldMap?.startDate || defaultApiFieldMap.startDate || 'start_date',
    endDateField: apiParamsFieldMap?.endDate || defaultApiFieldMap.endDate || 'end_date',
  };
};

const getUrlParamsFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  granularityField: string;
  startDateField: string;
  endDateField: string;
} => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);

  return {
    granularityField: urlParamsFieldMap?.granularity || defaultUrlFieldMap.granularity || 'granularity',
    startDateField: urlParamsFieldMap?.startDate || defaultUrlFieldMap.startDate || 'startDate',
    endDateField: urlParamsFieldMap?.endDate || defaultUrlFieldMap.endDate || 'endDate',
  };
};

const getNormalizedFilterValue = (
  filterValue: IRangePickerFilterValue | undefined,
  params?: IRangePickerMiddlewareParams,
): IRangePickerFilterValue | null => {
  const { granularity, startDate, endDate } = getRangePickerValue(filterValue);
  const resolvedGranularity =
    granularity || (params?.granularities?.length === 1 ? (params.granularities[0] as IGranularity) : '');

  return getValidatedRangePickerValue({
    granularity: resolvedGranularity as IGranularity,
    startDate,
    endDate,
    granularities: params?.granularities,
    startOfWeek: params?.startOfWeek,
  });
};

type RangePickerApiParams = IDateRangeFetchParams | IRecord<string>;

class RangePickerMiddlewareUtils
implements IFilterMiddlewareUtils<IRangePickerFilterValue, RangePickerApiParams, IRangePickerMiddlewareParams>
{
  convertToApiParams(
    item: IFilterItemConfig,
    filterValue?: IRangePickerFilterValue,
    params?: IRangePickerMiddlewareParams,
  ): RangePickerApiParams | null {
    const { granularityField, startDateField, endDateField } = getApiParamsFieldMap(item, params);
    const normalizedValue = getNormalizedFilterValue(filterValue, params);

    if (!normalizedValue) {
      return null;
    }

    return {
      [granularityField]: normalizedValue.granularity,
      [startDateField]: normalizedValue.startDate,
      [endDateField]: normalizedValue.endDate,
    };
  }

  getManagedUrlKeys(item: IFilterItemConfig, params?: IRangePickerMiddlewareParams): string[] {
    const { granularityField, startDateField, endDateField } = getUrlParamsFieldMap(item, params);

    return [granularityField, startDateField, endDateField].filter(Boolean);
  }

  deserializeUrlParams(
    item: IFilterItemConfig,
    searchParams: URLSearchParams,
    params?: IRangePickerMiddlewareParams,
  ): IRangePickerFilterValue | null {
    if (isEmpty(searchParams)) {
      return null;
    }

    const { granularityField, startDateField, endDateField } = getUrlParamsFieldMap(item, params);
    const granularity =
      searchParams.get(granularityField) ||
      (params?.granularities?.length === 1 ? (params.granularities[0] as IGranularity) : '');
    const startDate = searchParams.get(startDateField) || '';
    const endDate = searchParams.get(endDateField) || '';

    return getValidatedRangePickerValue({
      granularity,
      startDate,
      endDate,
      granularities: params?.granularities,
      startOfWeek: params?.startOfWeek,
    });
  }

  serializeUrlParams(
    item: IFilterItemConfig,
    filterValue?: IRangePickerFilterValue,
    params?: IRangePickerMiddlewareParams,
  ): IRecord<string> | null {
    const validatedValue = getNormalizedFilterValue(filterValue, params);

    if (!validatedValue) {
      return null;
    }

    const { granularityField, startDateField, endDateField } = getUrlParamsFieldMap(item, params);
    return {
      [granularityField]: validatedValue.granularity,
      [startDateField]: validatedValue.startDate,
      [endDateField]: validatedValue.endDate,
    };
  }
}

export default RangePickerMiddlewareUtils;
